import React, { useEffect } from "react";
import {
  Typography,
  Divider,
  Grid,
  Button,
  Paper,
  TextField,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import { ace } from "../theme";
import SignatureCapture from "../../SignatureCapture/SignatureCapture";

const line = (label, value) => (
  <Grid container spacing={1} style={{ marginBottom: 8 }}>
    <Grid item xs={12} sm={4}>
      <Typography variant="body2" style={{ color: ace.muted }}>
        {label}
      </Typography>
    </Grid>
    <Grid item xs={12} sm={8}>
      <Typography variant="body1" style={{ color: ace.charcoal, fontWeight: 500 }}>
        {value || "—"}
      </Typography>
    </Grid>
  </Grid>
);

const ReviewSubmitStep = ({
  value,
  items,
  vehicles,
  thankYou,
  onDownloadPdf,
  pdfLoading,
  submitted,
  signerName,
  setSignerName,
  acceptedTerms,
  setAcceptedTerms,
  signatureRef,
}) => {
  const vehicleLines = (vehicles || []).map((v) => String(v).trim()).filter(Boolean);

  useEffect(() => {
    if (!thankYou && value.customerName) {
      setSignerName((prev) => (prev && prev.trim() ? prev : value.customerName));
    }
  }, [thankYou, value.customerName, setSignerName]);

  return (
    <div>
      {thankYou ? (
        <>
          <Paper
            elevation={0}
            style={{
              textAlign: "center",
              padding: "36px 28px 32px",
              marginBottom: 28,
              borderRadius: 20,
              background: `linear-gradient(165deg, ${ace.white} 0%, #fff8f8 45%, #ffeef0 100%)`,
              border: `1px solid ${ace.grayBorder}`,
              boxShadow: "0 12px 40px rgba(196, 30, 58, 0.12)",
            }}
          >
            <CheckCircleOutlineIcon
              style={{
                fontSize: 64,
                color: ace.red,
                marginBottom: 16,
                filter: "drop-shadow(0 4px 12px rgba(196, 30, 58, 0.25))",
              }}
            />
            <Typography
              variant="h4"
              component="h2"
              style={{
                color: ace.red,
                fontWeight: 800,
                letterSpacing: "-0.02em",
                marginBottom: 12,
                fontSize: "1.75rem",
              }}
            >
              Thank you!
            </Typography>
            <Typography
              variant="h6"
              style={{
                color: ace.charcoal,
                fontWeight: 600,
                lineHeight: 1.55,
                marginBottom: 10,
                fontSize: "1.1rem",
              }}
            >
              Thanks for your information — our team will be in touch.
            </Typography>
            <Typography variant="body1" style={{ color: ace.muted, lineHeight: 1.65, maxWidth: 440, margin: "0 auto" }}>
              We&apos;ve received your move details and inventory. We&apos;ll follow up at{" "}
              <strong style={{ color: ace.charcoal }}>{value.email || "the email you provided"}</strong> to prepare
              your quotation.
            </Typography>
          </Paper>
          <Button
            variant="contained"
            size="large"
            startIcon={<GetAppIcon />}
            onClick={onDownloadPdf}
            disabled={pdfLoading}
            style={{
              background: ace.red,
              color: ace.white,
              fontWeight: 600,
              marginBottom: 20,
              padding: "12px 24px",
              borderRadius: 10,
              boxShadow: "0 8px 24px rgba(196, 30, 58, 0.35)",
            }}
          >
            {pdfLoading ? "Preparing PDF…" : "Download move inventory (PDF)"}
          </Button>
          <Typography variant="body2" style={{ color: ace.muted, marginBottom: 8 }}>
            You can safely close this page. If you used the same link before, your information is saved with us.
          </Typography>
        </>
      ) : (
        <>
          <Typography variant="h5" style={{ color: ace.black, fontWeight: 700, marginBottom: 8 }}>
            Review & submit
          </Typography>
          {!submitted ? (
            <Paper
              elevation={0}
              style={{
                padding: 16,
                marginBottom: 16,
                borderRadius: 12,
                border: `1px solid ${ace.grayBorder}`,
                background: ace.white,
              }}
            >
              <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>
                Electronic signature
              </Typography>
              <Typography variant="body2" style={{ color: ace.muted, marginBottom: 12 }}>
                Sign below to confirm your details are accurate. This acknowledgment is attached to your quotation request.
              </Typography>
              <TextField
                label="Full name (as signed)"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                variant="outlined"
                margin="dense"
                fullWidth
                style={{ marginBottom: 12 }}
              />
              <SignatureCapture
                ref={signatureRef}
                height={160}
                label="Signature"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    color="primary"
                  />
                }
                label="I confirm the information above is accurate."
              />
            </Paper>
          ) : null}
          <Typography variant="body2" style={{ color: ace.muted, marginBottom: 16 }}>
            Please confirm everything looks right. You can go back to edit any step, or download a
            copy of your inventory for your records.
          </Typography>
          <Button
            variant="outlined"
            size="medium"
            startIcon={<GetAppIcon />}
            onClick={onDownloadPdf}
            disabled={pdfLoading}
            style={{
              borderColor: ace.charcoal,
              color: ace.charcoal,
              fontWeight: 600,
              marginBottom: 20,
            }}
          >
            {pdfLoading ? "Preparing PDF…" : "Download move inventory (PDF)"}
          </Button>
        </>
      )}

      <Divider style={{ margin: "16px 0" }} />

      <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 8 }}>
        Contact
      </Typography>
      {line("Name", value.customerName)}
      {line("Email", value.email)}
      {line("Phone", value.phoneNumber)}
      {line("Move date", value.preferredMovingDate)}
      {line("Notes", value.notes)}

      <Typography variant="subtitle1" style={{ fontWeight: 700, margin: "20px 0 8px" }}>
        Origin
      </Typography>
      {line(
        "Address",
        [
          value.originAddress1,
          value.originAddress2,
          value.originCity,
          value.originState,
          value.originPincode,
          value.originCountry,
        ]
          .filter(Boolean)
          .join(", ")
      )}

      <Typography variant="subtitle1" style={{ fontWeight: 700, margin: "20px 0 8px" }}>
        Destination
      </Typography>
      {line(
        "Address",
        [
          value.destinationAddress1,
          value.destinationAddress2,
          value.destinationCity,
          value.destinationState,
          value.destinationPincode,
          value.destinationCountry,
        ]
          .filter(Boolean)
          .join(", ")
      )}

      {vehicleLines.length > 0 ? (
        <>
          <Typography variant="subtitle1" style={{ fontWeight: 700, margin: "20px 0 8px" }}>
            Vehicles
          </Typography>
          {vehicleLines.map((v, i) => (
            <Typography key={i} variant="body2" style={{ marginBottom: 4 }}>
              {v}
            </Typography>
          ))}
        </>
      ) : null}

      <Typography variant="subtitle1" style={{ fontWeight: 700, margin: "20px 0 8px" }}>
        Items
      </Typography>
      {(items || []).map((it, i) => (
        <Typography key={i} variant="body2" style={{ marginBottom: 4 }}>
          {it.itemName} × {it.quantity}
        </Typography>
      ))}
    </div>
  );
};

export default ReviewSubmitStep;
